import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { buildProviderSlug } from "@/lib/provider-slug";
import { ProviderPhoto } from "@/components/ProviderPhoto";
import {
  ArrowRight,
  MapPin,
  Phone,
  Star,
  Languages as LanguagesIcon,
} from "lucide-react";
import { findInsurance, type Provider } from "@/data/heallexa";
import { InsuranceNetworkBadge } from "@/components/InsuranceNetworkBadge";
import { TrustBadges } from "@/components/TrustBadges";
import { cn } from "@/lib/utils";

/**
 * A search result reads like a chart entry, not an ad. It answers the patient's
 * questions in the order they ask them — who is this, where are they, how good
 * are they, do they take my insurance, when can I be seen, how do I book — and
 * nothing is on the card that does not answer one of those.
 *
 * Three type sizes (name / body / meta) and one accent colour, spent on the
 * primary action, in-network status and open days. Structural devices have to
 * mean something: the top rule appears only on premium listings, so it reads as
 * paid placement rather than decorating all twenty cards.
 */

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const DAY_LETTERS = ["S", "M", "T", "W", "T", "F", "S"];

// meters → "3.2 mi" / "12 mi". ZIP-centroid based, so sub-mile is "< 1 mi".
function formatDistance(meters: number): string {
  const miles = meters / 1609.34;
  if (miles < 1) return "< 1 mi";
  return `${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi`;
}

// The badge reads "3.2 mi" visually, but "mi" is pronounced as a word by most
// screen readers ("my"), so the accessible name spells the unit out.
function distanceSpokenLabel(meters: number): string {
  const miles = meters / 1609.34;
  if (miles < 1) return "Less than 1 mile away";
  const n = miles < 10 ? miles.toFixed(1) : String(Math.round(miles));
  return `${n} miles away`;
}

type CalendarDay = { key: string; letter: string; date: number; open: boolean; today: boolean };

/**
 * The next seven days, with the practice's open days marked.
 *
 * Built in an effect rather than during render: SSR output is cached and served
 * for hours, so the server's "today" is regularly not the reader's "today" and
 * dating this inline would be a hydration mismatch. Reserves its height so the
 * card does not jump when the week fills in.
 */
function AvailabilityStrip({ days }: { days: string[] }) {
  const [week, setWeek] = useState<CalendarDay[]>([]);

  useEffect(() => {
    const open = new Set((days ?? []).map((d) => d.toLowerCase().slice(0, 3)));
    const today = new Date();
    setWeek(
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        return {
          key: `${d.getMonth()}-${d.getDate()}`,
          letter: DAY_LETTERS[d.getDay()],
          date: d.getDate(),
          open: open.has(DAY_KEYS[d.getDay()]),
          today: i === 0,
        };
      }),
    );
  }, [days]);

  // No days on file means nobody has told us the schedule. Say that, rather
  // than render seven greyed boxes that read as "closed all week".
  if ((days?.length ?? 0) === 0) {
    return <p className="text-[12px] text-muted-foreground">Availability on request</p>;
  }

  return (
    // Full width on phones so seven cells always fit the column; a fixed block
    // from sm up, where it sits in the free space beside the action buttons.
    <div className="min-h-[40px] w-full sm:w-[224px]" suppressHydrationWarning>
      <div className="flex gap-1" role="list" aria-label="Availability over the next 7 days">
        {week.map((d) => (
          <div
            key={d.key}
            role="listitem"
            aria-label={`${d.date}: ${d.open ? "available" : "unavailable"}`}
            className={cn(
              "flex flex-1 flex-col items-center rounded py-1 text-[11px] leading-tight",
              d.open ? "bg-primary/10 text-primary" : "text-muted-foreground/40",
              d.today && "ring-1 ring-inset ring-primary/40",
            )}
          >
            <span className="text-[10px] opacity-70" aria-hidden>
              {d.letter}
            </span>
            <span className="font-semibold">{d.date}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProviderCard({
  p,
  highlights = [],
  insuranceToken = null,
  insuranceLabel = "",
}: {
  p: Provider;
  highlights?: string[];
  /** Selected carrier or plan token, e.g. "aetna" or "aetna:choice-pos-ii". */
  insuranceToken?: string | null;
  /** Human name for that token, shown in the in-network badge. */
  insuranceLabel?: string;
}) {
  const claimed = p.listing_status !== "unclaimed";
  const identityVerified = !!(p as any).verified_badge;
  /**
   * The crown means money changed hands, so it reads the subscription rather
   * than `listing_status`.
   *
   * `listing_status = 'premium'` is a separate flag an admin can toggle by hand
   * (dashboard.admin.tsx "Make Premium"), and beta already has a row set that
   * way while sitting on plan='free', premium_subscription_status='none'. Using
   * it here showed a paid badge to someone who never paid.
   *
   * Lifetime is bought outright, so it has no ongoing subscription_status to
   * check — the plan alone is the proof. Matches dashboard.doctor.tsx:172.
   */
  const plan = (p as any).plan as string | undefined;
  const premium =
    plan === "lifetime_premium" ||
    ((plan === "premium_monthly" || plan === "premium_yearly") &&
      (p as any).premium_subscription_status === "active");
  const reviewsCount = Number((p as any).reviews_count ?? 0);
  const hasRealRating = reviewsCount > 0 && Number(p.rating) > 0;
  const rating = Number(p.rating) || 0;
  const npi = p.npi?.trim();
  const validNpi = npi && /^\d{10}$/.test(npi) ? npi : null;
  const profileSearch = insuranceToken ? { insurance: insuranceToken } : {};
  const distanceM = (p as any).distance_m != null ? Number((p as any).distance_m) : null;
  const distance = distanceM != null ? formatDistance(distanceM) : null;
  // Three fits one row at every width; a fourth pushed "+N more" onto its own
  // line on phones, spending a whole row on a count.
  const carriers = p.insurance.map(findInsurance).filter(Boolean).slice(0, 3) as { short: string; id: string }[];


  return (
      <article
        className={cn(
          "group relative cursor-pointer rounded-lg border border-border bg-card p-4 transition-all hover:border-primary/50 hover:shadow-sm sm:p-5",
          // The accent rule is reserved for paid placement, so it carries
          // meaning instead of striping every card in the list. Amber matches
          // the crown badge — one colour per concept.
          premium && "border-t-2 border-t-amber-500",
        )}
      >
        <Link
          to="/providers/$id"
          params={{ id: buildProviderSlug(p) }}
          search={profileSearch}
          aria-label={`View ${p.name} profile`}
          // The card is one big click target; keyboard users need to see it.
          className="absolute inset-0 z-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        />

        {/* Phone — photo + identity on row 1, everything else full width
            beneath. Tablet up — the portrait spans both rows on the left. */}
        <div className="pointer-events-none relative z-10 grid min-w-0 grid-cols-[96px_minmax(0,1fr)] gap-x-4 [&_a]:pointer-events-auto [&_button]:pointer-events-auto sm:grid-cols-[132px_minmax(0,1fr)] sm:gap-x-5 lg:grid-cols-[140px_minmax(0,1fr)]">
          {/* Portrait at its natural 4:5 ratio, with a hairline so a
              white-background upload cannot bleed into the card. */}
          <div className="col-start-1 row-start-1 min-w-0 sm:row-span-2">
            <div className="aspect-[4/5] overflow-hidden rounded-md border border-border bg-muted">
              <ProviderPhoto provider={p} className="h-full w-full object-cover" />
            </div>
            {/* Under the portrait from sm up, where the column is wide enough
                for a centred pair. On phones the column is only 96px and the
                extra row made the portrait read as the smaller thing on the
                card, so there the badges ride beside the address instead. */}
            <div className="hidden sm:block">
              <TrustBadges npi={p.npi} identityVerified={identityVerified} className="mt-2" />
            </div>
          </div>

          <div className="col-start-2 row-start-1 min-w-0">
            <Link
              to="/providers/$id"
              params={{ id: buildProviderSlug(p) }}
              search={profileSearch}
              // Clamped, never truncated, at every width. Practice names like
              // "Comprehensive Ophthalmology and Retina Associates of Southern
              // California" are common in NPPES data and a single cut line
              // identifies nobody. Two lines, then an ellipsis.
              //
              // No `block` here: it and line-clamp are both display utilities,
              // and block winning the cascade silently disabled the clamp.
              className="line-clamp-2 text-[16px] font-semibold leading-snug tracking-[-0.01em] hover:underline sm:text-[18px]"
            >
              {p.name}
            </Link>

            {/* Specialty only — it is what the patient searched for. The
                practice name is dropped: for solo listings NPPES repeats the
                provider's own name back, so the line read "Ashley Webster ·
                Ashley Webster". */}
            <p className="mt-1 line-clamp-2 text-[13px] leading-snug text-foreground/70">{p.specialty}</p>

            {/* Rating sits inline under the name rather than in a right-hand
                column: the column cost the name ~80px of width on every card,
                and most listings have no reviews to put there anyway. */}
            {/* Rating and distance share one row so the pin costs no vertical
                space on cards that already have a rating. */}
            {(hasRealRating || distance) && (
              <p className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[13px]">
                {hasRealRating && (
                  <span className="flex items-center gap-1">
                    <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" aria-hidden />
                    <span className="font-semibold">{rating.toFixed(1)}</span>
                    <span className="text-[12px] text-muted-foreground">({reviewsCount} reviews)</span>
                  </span>
                )}
                {/* Distance is the one thing on the card the patient cannot get
                    from the provider's own page, so it gets a filled chip
                    rather than a run-on clause at the end of the address. */}
                {distance && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[12px] font-medium text-primary"
                    aria-label={distanceSpokenLabel(distanceM!)}
                  >
                    <MapPin className="h-3 w-3" aria-hidden />
                    <span aria-hidden>{distance}</span>
                  </span>
                )}
              </p>
            )}

            <p className="mt-1 line-clamp-2 text-[12px] leading-snug text-muted-foreground">
              {/* Phones get the town, not the suite number: the street address
                  ate two clipped lines beside the portrait, and nobody picks a
                  doctor on the street name. */}
              <span className="sm:hidden">{[p.city, p.state].filter(Boolean).join(", ")}</span>
              <span className="hidden sm:inline">
                {[p.address, p.city, p.state, p.zip].filter(Boolean).join(", ")}
              </span>
            </p>

            <div className="sm:hidden">
              <TrustBadges
                npi={p.npi}
                identityVerified={identityVerified}
                className="mt-2 justify-start"
              />
            </div>
          </div>

          {/* Body: full width under both columns on phones, second column from
              sm up where the portrait is tall enough to sit beside it. */}
          <div className="col-span-2 col-start-1 row-start-2 mt-3 min-w-0 space-y-2.5 sm:col-span-1 sm:col-start-2 sm:mt-2.5">
            {/* Network status for the filtered insurance — shared with the
                provider profile so both surfaces say the same thing. */}
            <InsuranceNetworkBadge
              providerInsurance={p.insurance}
              token={insuranceToken}
              label={insuranceLabel}
            />

            {/* Carriers only when there are some. The old block spent a labelled
                row on "Not specified" for all but nine listings in the
                directory — a row of nothing, twenty times a page. */}
            {carriers.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                {carriers.map((ins) => (
                  <span
                    key={ins.id}
                    className="inline-flex h-6 items-center rounded bg-muted px-2 text-[11px] leading-none text-foreground/80"
                  >
                    {ins.short}
                  </span>
                ))}
                {p.insurance.length > carriers.length && (
                  <span className="text-[11px] text-muted-foreground">
                    +{p.insurance.length - carriers.length} more
                  </span>
                )}
              </div>
            )}

            {highlights.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {highlights.slice(0, 3).map((h) => (
                  <span
                    key={h}
                    className="inline-flex h-6 items-center rounded bg-primary/10 px-2 text-[11px] font-medium capitalize leading-none text-primary"
                  >
                    {h}
                  </span>
                ))}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border pt-2.5 text-[12px] text-muted-foreground">
              {p.phone ? (
                <a
                  href={`tel:${p.phone}`}
                  onClick={(e) => e.stopPropagation()}
                  className="flex min-w-0 items-center gap-1.5 hover:text-foreground hover:underline"
                >
                  <Phone className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="truncate">{p.phone}</span>
                </a>
              ) : (
                <span className="flex min-w-0 items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="truncate">{[p.city, p.state].filter(Boolean).join(", ")}</span>
                </span>
              )}
              {(p.languages?.length ?? 0) > 0 && (
                <span className="flex min-w-0 items-center gap-1.5">
                  <LanguagesIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="truncate">
                    {(p.languages ?? []).slice(0, 3).join(", ")}
                    {(p.languages?.length ?? 0) > 3 ? "…" : ""}
                  </span>
                </span>
              )}
            </div>

            {/* Actions on the left, the week pushed to the free space on their
                right. Stacks on phones, where there is no free space to use. */}
            <div className="flex flex-col gap-2.5 pt-0.5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <Link
                  to="/providers/$id"
                  params={{ id: buildProviderSlug(p) }}
                  search={profileSearch}
                  className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90 sm:flex-none"
                >
                  Book online <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                </Link>
                {!claimed && (
                  <Link
                    to="/providers/$id/claim"
                    params={{ id: buildProviderSlug(p) }}
                    className="inline-flex h-9 flex-1 items-center justify-center rounded-md border border-border px-4 text-[13px] font-medium transition-colors hover:bg-muted sm:flex-none"
                  >
                    Claim listing
                  </Link>
                )}
              </div>
              <AvailabilityStrip days={p.available_days ?? []} />
            </div>
          </div>
        </div>
      </article>
  );
}
